import type { ListingImportJob } from '../types/listing-import-job';
import { fetchListingImportJob } from './fetch-listing-import-job';
import {
  LISTING_IMPORT_POLL_CAP_MS,
  LISTING_IMPORT_POLL_MS,
} from './listing-import-job-refetch-interval';

export class ListingImportJobWaitTimeoutError extends Error {
  readonly importId: string;

  constructor(importId: string) {
    super('Listing import status polling timed out');
    this.name = 'ListingImportJobWaitTimeoutError';
    this.importId = importId;
  }
}

export async function waitForListingImportJob(
  importId: string,
  options?: {
    fetchJob?: (id: string) => Promise<unknown>;
    pollMs?: number;
    pollCapMs?: number;
    now?: () => number;
    sleep?: (ms: number) => Promise<void>;
  }
): Promise<ListingImportJob> {
  const fetchJob = options?.fetchJob ?? fetchListingImportJob;
  const pollMs = options?.pollMs ?? LISTING_IMPORT_POLL_MS;
  const pollCapMs = options?.pollCapMs ?? LISTING_IMPORT_POLL_CAP_MS;
  const now = options?.now ?? (() => Date.now());
  const sleep =
    options?.sleep ??
    ((ms: number) => new Promise(resolve => setTimeout(resolve, ms)));

  const startedAt = now();

  while (true) {
    try {
      const payload = await fetchJob(importId);
      const job = payload as ListingImportJob;

      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }
    } catch {
      // A single poll miss is not a terminal failure.
      // Keep waiting until the cap so a blip does not
      // fail a job that is still running.
    }

    if (now() - startedAt > pollCapMs) {
      throw new ListingImportJobWaitTimeoutError(importId);
    }

    await sleep(pollMs);
  }
}
